import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Blocks, Eye, FileText, Sparkles, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { getAdminProducts, getLandingPage } from "../../lib/api";
import type { AdminUser } from "../../types/content";
import { AdminHeader } from "./AdminUi";

export function AdminOverview({ user }: { user: AdminUser }) {
  const productQuery = useQuery({ queryKey: ["admin-products"], queryFn: getAdminProducts });
  const landingQuery = useQuery({ queryKey: ["public-landing"], queryFn: getLandingPage });
  const products = productQuery.data || [];
  const payload = landingQuery.data;
  const activeSections = payload
    ? Object.values(payload.site).filter((value) => typeof value === "object" && "isVisible" in value && value.isVisible).length
    : 0;

  return (
    <>
      <AdminHeader
        title={`Good day, ${user.name.split(" ")[0]}`}
        description="Here is what is happening across the Steria House website."
        action={
          <a className="button button-primary" href="/" target="_blank" rel="noreferrer">
            View website <Eye size={16} />
          </a>
        }
      />
      <div className="overview-grid">
        <article>
          <span className="overview-icon"><Blocks size={20} /></span>
          <strong>{products.length}</strong>
          <p>Total products</p>
          <small>{products.filter((item) => item.isActive).length} published</small>
        </article>
        <article>
          <span className="overview-icon"><FileText size={20} /></span>
          <strong>{activeSections}</strong>
          <p>Visible sections</p>
          <small>Landing page is live</small>
        </article>
        <article>
          <span className="overview-icon"><Sparkles size={20} /></span>
          <strong>{payload?.services.length || 0}</strong>
          <p>Capabilities</p>
          <small>Managed dynamically</small>
        </article>
        <article>
          <span className="overview-icon"><TrendingUp size={20} /></span>
          <strong>{payload?.statistics.length || 0}</strong>
          <p>Impact metrics</p>
          <small>Ready to update</small>
        </article>
      </div>
      <div className="overview-columns">
        <section className="admin-card">
          <div className="card-heading">
            <div>
              <h2>Product portfolio</h2>
              <p>Current public product status</p>
            </div>
            <Link to="/admin/products">Manage <ArrowRight size={15} /></Link>
          </div>
          <div className="overview-products">
            {products.slice(0, 4).map((product) => (
              <div key={product.id}>
                <span className="product-avatar">{product.name.slice(0, 1)}</span>
                <div><strong>{product.name}</strong><small>{product.category}</small></div>
                <i className={product.isActive ? "status-active" : "status-inactive"}>
                  {product.isActive ? "Live" : "Draft"}
                </i>
              </div>
            ))}
          </div>
        </section>
        <section className="admin-card quick-card">
          <div className="card-heading"><div><h2>Quick actions</h2><p>Keep the house current</p></div></div>
          <Link to="/admin/landing-page"><FileText size={17} /><span>Edit landing page<small>Copy, CTAs, visibility</small></span><ArrowRight size={16} /></Link>
          <Link to="/admin/products/new"><Blocks size={17} /><span>Add a new product<small>Product details and gallery</small></span><ArrowRight size={16} /></Link>
          <Link to="/admin/settings"><Sparkles size={17} /><span>Review SEO settings<small>Title and description</small></span><ArrowRight size={16} /></Link>
        </section>
      </div>
    </>
  );
}
